import { partnerStatementDo, partnerStatementDont, partnerFeedbackQuote } from '@/mocks/homeSections';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import FaqAccordion from '@/components/feature/FaqAccordion';

export default function PartnerStatement() {
  const anim = useScrollAnimation({ threshold: 0.12 });

  return (
    <section className="w-full py-16 md:py-24 bg-navy-900 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-80 h-80 bg-gold-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-navy-400/10 rounded-full blur-3xl"></div>

      <div
        ref={anim.ref}
        className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${anim.isVisible ? 'animate-visible' : ''}`}
      >
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
            <p className="text-xs font-medium text-gold-400 uppercase tracking-[0.2em] mb-3">
              Our Honest Position
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
              A Partner, <span className="text-gold-400">Not a Promise</span>
            </h2>
            <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-5 mb-6"></div>
            <p className="text-sm md:text-base text-cream-200 leading-relaxed">
              We want to be clear about what Leavitt Peak Press is and what it is not. We are an author&rsquo;s
              partner in building visibility &mdash; through global participation, international book fairs, and
              the slow, unglamorous work of staying present. We are not a guarantee. We are not a magic
              solution. We represent you honestly, and we ask you to expect exactly that.
            </p>
          </div>

          {/* Two columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* What we do */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gold-500/20 text-gold-400 shrink-0">
                  <i className="ri-check-line text-lg"></i>
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-white">What we genuinely do</h3>
                  <p className="text-xs text-cream-300 mt-0.5">The work we can stand behind</p>
                </div>
              </div>

              <ul className="space-y-5">
                {partnerStatementDo.map((item) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-gold-400/10 text-gold-400 shrink-0 mt-0.5">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-sm text-cream-300 leading-relaxed">{item.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* What we don't claim */}
            <div className="bg-navy-950/40 border border-white/10 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 text-cream-200 shrink-0">
                  <i className="ri-close-line text-lg"></i>
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-white">What we never claim</h3>
                  <p className="text-xs text-cream-300 mt-0.5">Tap any line for the honest answer</p>
                </div>
              </div>

              <FaqAccordion
                items={partnerStatementDont.map((item) => ({
                  question: item.title,
                  answer: item.description,
                }))}
                variant="dark"
                defaultOpen={0}
              />
            </div>
          </div>

          {/* Feedback note */}
          <div className="mt-6 lg:mt-8 bg-gradient-to-r from-gold-500/12 via-gold-500/6 to-transparent border border-gold-400/25 rounded-2xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-start gap-5">
              <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold-500/20 text-gold-400 shrink-0">
                <i className="ri-chat-quote-line text-xl"></i>
              </span>
              <div className="flex-1">
                <h3 className="text-base md:text-lg font-semibold text-white mb-2">
                  On reviews, and where they come from
                </h3>
                <p className="text-sm text-cream-200 leading-relaxed mb-3">
                  Every word of feedback you see from us comes from an author who actually worked with
                  Leavitt Peak Press &mdash; people who sat in the same meetings, saw the same booth, and lived
                  the same process. We publish the experience as it was given to us.
                </p>
                <p className="text-sm text-cream-200 leading-relaxed mb-3">
                  If you search hard enough, you may not find a long trail of complaints written by our
                  authors. That is not because we curate a perfect story. It is because we would rather be
                  judged on the relationships we actually built than on scattered remarks from people who
                  never worked with us &mdash; or from competitors with an interest in saying otherwise.
                </p>
                <p className="text-sm text-gold-300 leading-relaxed font-medium">
                  Authentic experience, in the author&rsquo;s own words. That is the only kind of testimonial
                  this press will ever put its name behind.
                </p>

                <div className="mt-5 pt-5 border-t border-gold-400/20 flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full overflow-hidden border border-gold-400/40 shrink-0">
                    <img
                      src={partnerFeedbackQuote.avatar}
                      alt={partnerFeedbackQuote.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm italic text-cream-100 leading-relaxed">
                      &ldquo;{partnerFeedbackQuote.quote}&rdquo;
                    </p>
                    <p className="mt-2 text-xs text-gold-300 font-medium">
                      {partnerFeedbackQuote.author} &middot; {partnerFeedbackQuote.role}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10 text-center">
            <p className="text-sm text-cream-300 mb-5">
              Want to know exactly what we would do for your title &mdash; and what we would not?
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/contact-us"
                className="w-full sm:w-auto px-7 py-3.5 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Ask Us Straight
              </a>
              <a
                href="/about-us"
                className="w-full sm:w-auto px-7 py-3.5 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Who We Are
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}